// ─── Thermal Receipt Printer (80mm) ───────────────────────────────────
// Generates a 80mm-wide receipt-style HTML page and triggers the browser
// print dialog. The browser sends the job to whichever printer the user picks
// — including Bluetooth thermal printers (HOIN, GOOJPRT, etc.) paired with
// the OS. On Android, Chrome can print to any printer registered via Mopria
// or the manufacturer's print service.
//
// Layout is intentionally minimal: monospace, RTL, 1 column, 9pt font, no
// background colors (thermal printers ignore them anyway).

const fmtNum = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const BASE_STYLES = `
  @page { size: 80mm 297mm; margin: 1mm 0; }
  @media print {
    @page { size: 80mm 297mm; margin: 1mm 0; }
    html, body { width: 76mm !important; }
  }
  html, body { margin: 0; padding: 0; font-family: 'Tahoma','Cairo','Segoe UI',monospace; color: #000; background: #fff; }
  body { width: 76mm; max-width: 76mm; padding: 0 2mm; font-size: 11pt; line-height: 1.5; word-wrap: break-word; overflow-wrap: anywhere; }
  * { box-sizing: border-box; max-width: 100%; }
  .center { text-align: center; }
  .right { text-align: right; }
  .left { text-align: left; }
  .bold { font-weight: 900; }
  .big { font-size: 14pt; word-break: break-all; }
  .small { font-size: 9pt; color: #000; font-weight: bold; }
  .x-small { font-size: 8pt; color: #000; font-weight: bold; }
  .divider { border-top: 1px dashed #000; margin: 4px 0; }
  .double { border-top: 3px double #000; margin: 5px 0; }
  /* High-contrast info row: label small-gray on top, value big-bold below */
  .info-row { margin: 6px 0; padding-bottom: 4px; border-bottom: 1px dotted #000; }
  .info-row .label { font-size: 10pt; color: #000; font-weight: 900; display: block; margin-bottom: 2px; }
  .info-row .value { font-size: 13pt; font-weight: 900; color: #000; display: block; word-break: break-word; }
  .info-row.ltr .value { direction: ltr; text-align: right; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; word-wrap: break-word; }
  td, th { padding: 2px 1px; vertical-align: top; overflow-wrap: anywhere; }
  table tr td:first-child { width: 32%; color: #000; font-weight: bold; }
  table tr td:last-child { width: 68%; font-weight: bold; }
  .total-box { border: 3px solid #000; padding: 8px 6px; margin: 8px 0; text-align: center; }
  .total-box .label-mb { font-size: 11pt; color: #000; margin-bottom: 4px; display: block; font-weight: 900; }
  .total-box .value-big { font-size: 18pt; font-weight: 900; display: block; }
  .stamp { margin-top: 6px; padding-top: 6px; border-top: 1px dashed #000; font-size: 9pt; text-align: center; }
  h1 { font-size: 16pt; margin: 4px 0 2px; font-weight: 900; }
  h2 { font-size: 13pt; margin: 6px 0 3px; }
  .feed { height: 18mm; }
`;

/** Open a new window with the receipt HTML, autoload print, then close. */
const openPrintWindow = (innerHTML, title = "Jumlaty") => {
  const w = window.open("", "_blank", "width=400,height=700");
  if (!w) {
    alert("منع المتصفّح فتح نافذة الطباعة. الرجاء السماح للنوافذ المنبثقة من هذا الموقع.");
    return;
  }
  w.document.write(`<!doctype html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>${BASE_STYLES}</style>
</head>
<body>
  ${innerHTML}
  <script>
    window.addEventListener('load', function() {
      // Wait for fonts + layout to settle before showing print dialog. Without
      // this, the browser sometimes measures the page before Tahoma loads and
      // the last line gets pushed off the next "logical page" → blank cut.
      setTimeout(function() {
        window.focus();
        window.print();
        setTimeout(function(){ try { window.close(); } catch(e){} }, 800);
      }, 500);
    });
  </script>
</body>
</html>`);
  w.document.close();
};

const HEADER_HTML = `
  <h1 class="center bold">جملتي</h1>
  <p class="center small">jumlaty.online</p>
  <div class="divider"></div>
`;

const FOOTER_HTML = `
  <div class="stamp">
    شكراً لتعاملكم<br/>
    <span class="x-small">jumlaty.online</span>
  </div>
  <div class="feed"></div>
`;

/* ────── Voucher kind labels (mirrors EmployeesTab) ────── */
const VOUCHER_KIND_LABEL = {
  salary_disbursement: "سند صرف راتب",
  advance_out: "سند دفع سلفة",
  advance_in: "سند قبض سلفة",
};

/**
 * Print an employee salary/advance voucher.
 * @param voucher { id, kind, amount, note, created_at, ... }
 * @param employee { full_name, role, phone, ... }
 */
export const printEmployeeVoucher = (voucher, employee) => {
  const kindLabel = VOUCHER_KIND_LABEL[voucher.kind] || "سند موظف";
  const date = (voucher.created_at || new Date().toISOString()).slice(0, 16).replace("T", " ");
  const voucherNo = voucher.voucher_number
    ? String(voucher.voucher_number)
    : (voucher.id || "").slice(0, 6).toUpperCase();
  const sign = voucher.kind === "advance_in" || voucher.kind === "salary_disbursement" ? "-" : "+";
  // For advance vouchers, show the running advance balance on the receipt so
  // the employee sees how much they still owe (or are owed) after this entry.
  const isAdvance = voucher.kind === "advance_in" || voucher.kind === "advance_out";
  const remaining = isAdvance && employee && employee.advance_remaining != null
    ? Number(employee.advance_remaining)
    : null;
  const remainingLabel = remaining != null
    ? (remaining > 0
        ? "المتبقي من السلفة عليك"
        : remaining < 0
          ? "رصيد لصالحك (دفعت أكثر)"
          : "السلفة مسدّدة بالكامل")
    : null;
  const body = `
    ${HEADER_HTML}
    <h2 class="center bold">${kindLabel}</h2>
    <div class="double"></div>

    <div class="info-row ltr">
      <span class="label">رقم السند</span>
      <span class="value">#${voucherNo}</span>
    </div>
    <div class="info-row ltr">
      <span class="label">التاريخ</span>
      <span class="value">${date}</span>
    </div>
    <div class="info-row">
      <span class="label">الموظف</span>
      <span class="value">${employee?.full_name || "—"}</span>
    </div>
    ${employee?.role ? `
    <div class="info-row">
      <span class="label">الوظيفة</span>
      <span class="value">${employee.role}</span>
    </div>` : ""}
    ${employee?.phone ? `
    <div class="info-row ltr">
      <span class="label">الهاتف</span>
      <span class="value">${employee.phone}</span>
    </div>` : ""}

    <div class="total-box">
      <span class="label-mb">المبلغ</span>
      <span class="value-big" dir="ltr">${sign}${fmtNum(voucher.amount)} د.ع</span>
    </div>

    ${remainingLabel ? `
    <div class="total-box" style="border-style:dashed; border-width:2px;">
      <span class="label-mb">${remainingLabel}</span>
      <span class="value-big" dir="ltr">${fmtNum(Math.abs(remaining))} د.ع</span>
    </div>` : ""}

    ${voucher.note ? `<div class="info-row"><span class="label">ملاحظة</span><span class="value" style="font-size:11pt;">${voucher.note}</span></div>` : ""}

    <div class="divider"></div>
    <p class="small">توقيع المستلم: ____________________</p>
    <p class="small">توقيع المسؤول: ____________________</p>
    ${FOOTER_HTML}
  `;
  openPrintWindow(body, kindLabel);
};

/**
 * Print an employee info card — name, role, phone, balance, address, start date.
 */
export const printEmployeeInfo = (emp, summary = null) => {
  const today = new Date().toISOString().slice(0, 16).replace("T", " ");
  const body = `
    ${HEADER_HTML}
    <h2 class="center bold">بطاقة موظف</h2>
    <div class="double"></div>

    <div class="info-row">
      <span class="label">الاسم</span>
      <span class="value">${emp.full_name || "—"}</span>
    </div>
    ${emp.role ? `<div class="info-row"><span class="label">الوظيفة</span><span class="value">${emp.role}</span></div>` : ""}
    ${emp.phone ? `<div class="info-row ltr"><span class="label">الهاتف</span><span class="value">${emp.phone}</span></div>` : ""}
    ${emp.address ? `<div class="info-row"><span class="label">العنوان</span><span class="value" style="font-size:11pt;">${emp.address}</span></div>` : ""}
    ${emp.salary_amount ? `<div class="info-row ltr"><span class="label">الراتب الأساسي</span><span class="value">${fmtNum(emp.salary_amount)} د.ع</span></div>` : ""}
    ${emp.start_date ? `<div class="info-row ltr"><span class="label">تاريخ البداية</span><span class="value">${(emp.start_date || "").slice(0, 10)}</span></div>` : ""}

    ${summary ? `
      <div class="divider"></div>
      <h2 class="center">الكشف المالي</h2>
      <div class="info-row ltr"><span class="label">مجموع الرواتب المصروفة</span><span class="value">${fmtNum(summary.total_salary || 0)} د.ع</span></div>
      <div class="info-row ltr"><span class="label">سلف مدفوعة</span><span class="value">${fmtNum(summary.total_advance_out || 0)} د.ع</span></div>
      <div class="info-row ltr"><span class="label">سلف مقبوضة</span><span class="value">${fmtNum(summary.total_advance_in || 0)} د.ع</span></div>
      <div class="total-box">
        <span class="label-mb">رصيد السلف الحالي</span>
        <span class="value-big" dir="ltr">${fmtNum(summary.advance_balance || 0)} د.ع</span>
      </div>
    ` : ""}

    <p class="small">طُبع في: <span dir="ltr">${today}</span></p>
    ${FOOTER_HTML}
  `;
  openPrintWindow(body, `بطاقة ${emp.full_name || "موظف"}`);
};

/**
 * Print a full advance ledger (statement) for an employee — every advance_in /
 * advance_out movement with running balance.
 */
export const printAdvanceLedger = (employee, vouchers) => {
  const advances = (vouchers || []).filter((v) => v.kind === "advance_in" || v.kind === "advance_out")
    .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  let running = 0;
  const rows = advances.map((v) => {
    const out = v.kind === "advance_out" ? Number(v.amount || 0) : 0;
    const inn = v.kind === "advance_in" ? Number(v.amount || 0) : 0;
    running += out - inn; // balance = paid out − paid back
    return `
      <tr>
        <td dir="ltr" class="x-small">${(v.created_at || "").slice(2, 10)}</td>
        <td class="x-small">${out ? fmtNum(out) : "—"}</td>
        <td class="x-small">${inn ? fmtNum(inn) : "—"}</td>
        <td class="x-small" dir="ltr">${fmtNum(running)}</td>
      </tr>`;
  }).join("");
  const total = running;
  const today = new Date().toISOString().slice(0, 16).replace("T", " ");
  const body = `
    ${HEADER_HTML}
    <h2 class="center bold">كشف سلف الموظف</h2>
    <p class="center small bold">${employee?.full_name || "—"}</p>
    <div class="divider"></div>
    <table style="width:100%; table-layout:fixed; border-collapse:collapse;">
      <thead>
        <tr class="bold" style="border-bottom:1px solid #000;">
          <td style="width:28%; padding:2px 1px;">التاريخ</td>
          <td style="width:24%; padding:2px 1px;">دفع</td>
          <td style="width:24%; padding:2px 1px;">قبض</td>
          <td style="width:24%; padding:2px 1px;">الرصيد</td>
        </tr>
      </thead>
      <tbody>${rows || `<tr><td colspan="4" class="center small">لا توجد سلف</td></tr>`}</tbody>
    </table>
    <div class="divider"></div>
    <div class="total-box">
      <span class="label-mb">الرصيد المتبقي على الموظف</span>
      <span class="value-big" dir="ltr">${fmtNum(total)} د.ع</span>
    </div>
    <p class="small">طُبع في: <span dir="ltr">${today}</span></p>
    ${FOOTER_HTML}
  `;
  openPrintWindow(body, `كشف سلف ${employee?.full_name || "موظف"}`);
};

export default { printEmployeeVoucher, printEmployeeInfo, printAdvanceLedger };
